// OpenRouter API Configuration
// This file centralizes all OpenRouter API related settings

// Import the customFetch function to make backend API calls
import { customFetch } from '../utils/api';

// Type declaration for process.env (for environments where process might not be available)
declare const process: {
  env: {
    REACT_APP_OPENROUTER_API_KEY?: string;
  };
} | undefined;

export interface OpenRouterConfig {
  apiUrl: string;
  apiKey: string;
  defaultModel: string;
  timeout: number;
  temperature: number;
  maxTokens: number;
}

export interface OpenRouterModel {
  id: string;
  name: string;
  description: string;
}




// Available models for Reddit post scoring
export const AVAILABLE_MODELS: OpenRouterModel[] = [
  {
    id: "google/gemini-2.0-flash-lite-001",
    name: "gemini-2.0-flash-lite-001",
    description: "Fast and efficient for analysis tasks"
  },
  {
    id: "moonshotai/kimi-k2",
    name: "kimi-k2",
    description: "Fast and efficient for analysis tasks"
  },
  {
    id: "google/gemini-2.5-flash-lite-preview-06-17",
    name: "Gemini 2.5 Flash Lite",
    description: "Fast and efficient for analysis tasks"
  },
  {
    id: "qwen/qwen3-235b-a22b-2507",
    name: "qwen3-235b-a22b-2507",
    description: "Balanced performance and capability"
  },
  {
    id: "openai/gpt-4o-mini",
    name: "GPT-4o Mini",
    description: "Cost-effective GPT-4 class model"
  },
  {
    id: "openai/gpt-4o",
    name: "GPT-4o",
    description: "Latest GPT-4 model with multimodal capabilities"
  }
];

// Default configuration
export const DEFAULT_OPENROUTER_CONFIG: OpenRouterConfig = {
  apiUrl: "https://openrouter.ai/api/v1/chat/completions",
  apiKey: (typeof process !== 'undefined' && process.env?.REACT_APP_OPENROUTER_API_KEY) || "",
  defaultModel: "google/gemini-2.0-flash-lite-001", // Updated to match available models
  timeout: 120000, // 2 minutes
  temperature: 0.1, // Low temperature for precise analysis
  maxTokens: 4000
};

// Get OpenRouter configuration (will be populated by fetchOpenRouterConfig)
let runtimeConfig: OpenRouterConfig = { ...DEFAULT_OPENROUTER_CONFIG };

export const getOpenRouterConfig = (): OpenRouterConfig => {
  return runtimeConfig;
};

// Fetch OpenRouter configuration from backend
export const fetchOpenRouterConfig = async (): Promise<OpenRouterConfig> => {
  try {
    const apiUrl = '/api/llm/providers/openrouter/';
    console.log('🔄 Fetching OpenRouter config from backend...');
    console.log('🌐 Request URL:', apiUrl);
    
    // Use customFetch to make the request to the backend server
    const response = await customFetch(apiUrl);
    
    console.log('📡 Backend response status:', response.status);
    console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));
    
    if (response.ok) {
      const responseText = await response.text();
      console.log('📄 Raw response text (first 200 chars):', responseText.substring(0, 200));
      
      let providerData;
      try {
        providerData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ JSON parse error:', parseError);
        console.error('❌ Response was not valid JSON, got:', responseText.substring(0, 500));
        throw new Error('Invalid JSON response from server');
      }
      console.log('📋 Backend provider data:', {
        name: providerData.name,
        api_url: providerData.api_url,
        has_api_key: providerData.has_api_key,
        api_key_length: providerData.api_key ? providerData.api_key.length : 0
      });
      
      // Validate that we have an API key
      if (!providerData.api_key) {
        console.error('❌ Backend provider has no API key configured');
        throw new Error('Backend provider has no API key configured');
      }
      
      // Update runtime configuration with backend data
      runtimeConfig = {
        ...DEFAULT_OPENROUTER_CONFIG,
        apiKey: providerData.api_key,
        apiUrl: providerData.api_url || DEFAULT_OPENROUTER_CONFIG.apiUrl
      };
      
      console.log('✅ OpenRouter config loaded from backend database');
      return runtimeConfig;
    } else {
      const errorText = await response.text();
      console.warn('⚠️ Backend OpenRouter config request failed:', response.status);
      console.warn('⚠️ Response body (first 500 chars):', errorText.substring(0, 500));
    }
  } catch (error) {
    console.warn('⚠️ Failed to fetch OpenRouter config from backend:', error);
  }
  
  // Fallback to environment variables or localStorage
  runtimeConfig = {
    ...DEFAULT_OPENROUTER_CONFIG,
    apiKey: (typeof process !== 'undefined' && process.env?.REACT_APP_OPENROUTER_API_KEY) ||
             localStorage.getItem('openrouter_api_key') || 
             DEFAULT_OPENROUTER_CONFIG.apiKey
  };
  
  console.log('📋 Using fallback OpenRouter configuration');
  return runtimeConfig;
};

// Validate configuration
export const validateOpenRouterConfig = (config: OpenRouterConfig): boolean => {
  console.log('🔍 Validating OpenRouter configuration:', {
    hasApiKey: !!config.apiKey,
    apiKeyLength: config.apiKey ? config.apiKey.length : 0,
    apiKeyPrefix: config.apiKey ? config.apiKey.substring(0, 10) + '...' : 'none',
    apiUrl: config.apiUrl
  });
  
  if (!config.apiKey) {
    console.error("❌ OpenRouter API key is not configured");
    return false;
  }
  if (!config.apiUrl) {
    console.error("❌ OpenRouter API URL is not configured");
    return false;
  }
  
  console.log('✅ OpenRouter configuration is valid');
  return true;
};

// Helper function to get model name by ID
export const getModelName = (modelId: string): string => {
  const model = AVAILABLE_MODELS.find(m => m.id === modelId);
  return model ? model.name : modelId;
};